import React from 'react';
import { EscalationStatus } from '@/types/rental';
import { RungTally, STALE_AFTER_MINUTES } from '../rentTrackingDisplay';

export interface TrackingMapLegendProps {
  /** Every rung present in the loaded rows, harshest first. */
  tallies: RungTally[];
  /** Rungs currently kept. Empty means all of them. */
  selected: EscalationStatus[];
  onToggle: (rung: EscalationStatus) => void;
  onClear: () => void;
  className?: string;
}

/**
 * What the dots on the map mean, and the only place the rungs can be
 * separated from each other.
 *
 * The rungs carry three different jobs — "Wajib dimatikan" and "Sudah
 * dimatikan" are still desk work, "Perlu ditarik" sends someone out to the
 * unit — so each row doubles as a filter: clicking one narrows the map, the
 * table and the area breakdown to that job alone. Counts come from the
 * unfiltered rows, so a row never disappears just because it was filtered out.
 */
export const TrackingMapLegend: React.FC<TrackingMapLegendProps> = ({
  tallies,
  selected,
  onToggle,
  onClear,
  className = '',
}) => {
  if (tallies.length === 0) return null;
  const filtering = selected.length > 0;

  return (
    <div
      className={`w-[216px] rounded-xl border border-gray-200 bg-white/95 p-2 shadow-lg backdrop-blur ${className}`}
    >
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Status unit
        </span>
        {filtering && (
          <button
            type="button"
            onClick={onClear}
            className="text-[11px] font-medium text-primary hover:underline"
          >
            Tampilkan semua
          </button>
        )}
      </div>

      <ul className="flex flex-col">
        {tallies.map((tally) => {
          // Nothing chosen means everything is shown, so every row reads as
          // active rather than every row reading as muted.
          const active = !filtering || selected.includes(tally.rung);
          return (
            <li key={tally.rung}>
              <button
                type="button"
                aria-pressed={filtering && selected.includes(tally.rung)}
                onClick={() => onToggle(tally.rung)}
                title={
                  tally.drawn < tally.units
                    ? `${tally.units - tally.drawn} unit belum pernah kirim koordinat, jadi tidak ada titiknya di peta.`
                    : undefined
                }
                className={`flex w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-gray-50 ${
                  active ? '' : 'opacity-40'
                }`}
              >
                <span
                  aria-hidden
                  className="h-3 w-3 flex-shrink-0 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: tally.color }}
                />
                <span className="flex-1 truncate text-[12px] text-gray-800">
                  {tally.label}
                </span>
                <span className="text-[12px] font-semibold tabular-nums text-gray-900">
                  {tally.units}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* The two marks that ride on top of the rung colour, so a purple ring
          or a faded dot is not read as a fourth status. */}
      <div className="mt-1.5 flex flex-col gap-1 border-t border-gray-100 px-1.5 pt-1.5 text-[11px] text-gray-600">
        <span className="flex items-center gap-2">
          <span
            aria-hidden
            className="h-3 w-3 flex-shrink-0 rounded-full bg-gray-400"
            style={{ border: '3px solid #5e2aac' }}
          />
          Sudah ada case repo
        </span>
        <span className="flex items-center gap-2">
          <span
            aria-hidden
            className="h-3 w-3 flex-shrink-0 rounded-full border-2 border-white bg-gray-400 opacity-50 shadow"
          />
          Fix &gt; {STALE_AFTER_MINUTES} menit
        </span>
      </div>
    </div>
  );
};

export default TrackingMapLegend;
